import { useAccount, useMsal } from '@azure/msal-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import BulbIcon from '../components/common/icons/BulbIcon';
import LogoIcon from '../components/common/icons/LogoIcon';
import { loginRequest } from '../authConfig';

const PageContainer = styled.div`
  min-height: 100vh;
  min-width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #2B7A9B 0%, #8B3B8B 100%);
  position: relative;
  overflow: hidden;
`;

const Logo = styled.div`
  position: absolute;
  top: 20px;
  left: auto;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 20px;
`;

const WelcomeText = styled.h1`
  font-size: 160px;
  color: white;
  font-weight: 400;
  margin: 0;
  line-height: 121%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

const BrandContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const BrandIcon = styled.div`
  width: 62.5px;
  height: 60px;
  background-color: #00E5FF;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 20px rgba(0, 229, 255, 0.3);
`;

const BrandText = styled.div`
  font-size: 78px;
  color: white;
  line-height: 121%;
  font-weight: 400;
  display: flex;
  align-items: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;

  span:first-child {
    font-weight: 400;
  }

  span:last-child {
    font-weight: 600;
    background: linear-gradient(90deg, #0CF3FF 0%, #07EBC9 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const LoadingText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  margin-top: 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #00E5FF;
  animation: spin 1s linear infinite;
  margin-top: 200px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorIcon = () => (
  <svg width="70" height="70" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M28.9378 10.5C31.6321 5.83332 38.3679 5.83333 41.0622 10.5L59.2487 42C61.943 46.6667 58.5751 52.5 53.1865 52.5H16.8135C11.4249 52.5 8.05699 46.6667 10.7513 42L28.9378 10.5Z" fill="#FB155F" />
    <path d="M35 19C36.423 19 37.5452 20.2108 37.4373 21.6298L36.5251 33.6203C36.4645 34.4177 35.7997 35.0338 35 35.0338C34.2003 35.0338 33.5355 34.4177 33.4749 33.6203L32.5627 21.6298C32.4548 20.2108 33.577 19 35 19ZM32 40.8003C32 39.933 32.2857 39.1946 32.8571 38.5851C33.4505 37.9522 34.1648 37.6357 35 37.6357C35.8352 37.6357 36.5385 37.9522 37.1099 38.5851C37.7033 39.1946 38 39.933 38 40.8003C38 41.6676 37.7033 42.4177 37.1099 43.0506C36.5385 43.6835 35.8352 44 35 44C34.1868 44 33.4835 43.6835 32.8901 43.0506C32.2967 42.4177 32 41.6676 32 40.8003Z" fill="white" />
  </svg>
);

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: white;
  margin-top: 200px;
`;

const ErrorMessage = styled.h2`
  color: white;
  font-size: 20px;
  font-weight: 500;
  margin: 0;
`;

const Login: React.FC = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const account = useAccount(instance.getActiveAccount() ?? undefined);
  const [authError, setAuthError] = useState(false);

  useEffect(() => {
    if (account) {
      navigate('/dashboard');
    } else {
      // If not logged in, show login popup
      instance.loginPopup(loginRequest)
        .then((response) => {
          instance.setActiveAccount(response.account);
          navigate('/dashboard');
        })
        .catch((error) => {
          setAuthError(true);
          console.error('Login error:', error);
        });
    }
  }, [instance, account, navigate]);

  return (
    <PageContainer>
      <Logo>
        <LogoIcon />
      </Logo>
      <ContentContainer>
        <WelcomeText>Welcome to</WelcomeText>
        <BrandContainer>
          <BrandIcon>
            <BulbIcon />
          </BrandIcon>
          <BrandText>
            <span>Eyepax</span>
            <span>Insight</span>
          </BrandText>
        </BrandContainer>
        {authError && (
          <ErrorContainer>
            <ErrorIcon />
            <ErrorMessage>Sorry, you don't have access to this portal.</ErrorMessage>
          </ErrorContainer>
        )}
        {!authError && (
          <>
            <LoadingSpinner />
            <LoadingText>We are verifying your credentials..</LoadingText>
          </>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default Login; 